import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import type { SortingState } from '@tanstack/table-core';
import { useState } from 'react';

import {
  DataTable,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import {
  type CustomerListRow,
  type CustomerSort,
  defaultCustomersFilters,
  listCustomers,
} from '@/lib/customers';

export const Route = createFileRoute('/customers/')({
  component: CustomersPage,
});

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const column = dataTableColumnHelper<CustomerListRow>();

/**
 * Which columns the server can order by.
 *
 * `search` orders in Postgres, so a header is sortable only when there is a
 * sort key behind it — the rest render as plain labels rather than as controls
 * that would silently reorder one page of a hundred.
 */
const SORT_KEYS: Partial<Record<string, CustomerSort>> = {
  name: 'name',
  totalOrders: 'orders',
  totalSpentCents: 'spent',
};

const columns = column.columns([
  column.accessor('lastName', {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        to="/customers/$customerId"
        params={{ customerId: row.original.id }}
        className="block truncate font-medium hover:underline"
      >
        {[row.original.firstName, row.original.lastName].filter(Boolean).join(' ') || '—'}
      </Link>
    ),
  }),

  column.accessor('email', {
    id: 'contact',
    header: 'Contact',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-muted-foreground block truncate text-sm">
        {row.original.email ?? row.original.phone ?? '—'}
      </span>
    ),
  }),

  column.accessor('customerType', {
    header: 'Type',
    enableSorting: false,
    cell: ({ getValue }) => (
      <Badge variant="secondary" className="capitalize">
        {getValue().replace('_', ' ')}
      </Badge>
    ),
  }),

  column.accessor('totalOrders', {
    header: 'Orders',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => getValue().toLocaleString(),
  }),

  column.accessor('totalSpentCents', {
    header: 'Spent',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => usd(getValue()),
  }),
]);

/**
 * Customers.
 *
 * Server-paginated on purpose. Products sync into a TanStack DB collection
 * because the catalog is bounded and instant filtering is the win; this table
 * is shopper PII and grows without bound, so the ported loader's filtering,
 * column allowlist, ordering and paging stay on the server and only one page
 * is transferred.
 *
 * That is `manualPagination` and `manualSorting`: the shared shell draws the
 * page and owns the controls, the query decides what is on it.
 */
function CustomersPage() {
  const { activeAccountId } = useAuth();
  const navigate = useNavigate();
  // The query's own default is 20; the console shows 25 everywhere else, and a
  // list that pages differently from its neighbours is just a surprise.
  const [filters, setFilters] = useState(() => ({ ...defaultCustomersFilters(), perPage: 25 }));
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['customers', activeAccountId, filters],
    queryFn: () => listCustomers(activeAccountId as string, filters),
    enabled: Boolean(activeAccountId),
    // Keep the current page visible while the next one loads, so paging does
    // not flash an empty table.
    placeholderData: keepPreviousData,
  });

  function applySearch(value: string) {
    setSearch(value);
    // Any filter change resets to page 1; staying on page 7 of a narrower
    // result set would show an empty table.
    setFilters((current) => ({ ...current, q: value, page: 1 }));
  }

  const total = data?.total ?? 0;

  // `created` is the default order and has no column of its own, so nothing is
  // marked sorted until the operator picks a header.
  const sortedBy = Object.entries(SORT_KEYS).find(([, key]) => key === filters.sort)?.[0];

  const sorting: SortingState = sortedBy ? [{ id: sortedBy, desc: filters.dir === 'desc' }] : [];

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: data?.rows ?? [],
    manualPagination: true,
    manualSorting: true,
    rowCount: total,
    state: {
      pagination: { pageIndex: filters.page - 1, pageSize: filters.perPage },
      sorting,
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({ pageIndex: filters.page - 1, pageSize: filters.perPage })
          : updater;

      setFilters((current) => ({ ...current, page: next.pageIndex + 1, perPage: next.pageSize }));
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const sort = next[0] ? SORT_KEYS[next[0].id] : undefined;

      // Reordering moves rows across page boundaries, so page 7 of the old
      // order means nothing in the new one.
      setFilters((current) => ({
        ...current,
        sort: sort ?? 'created',
        dir: (next[0]?.desc ?? true) ? 'desc' : 'asc',
        page: 1,
      }));
    },
    // A column with no server-side sort key stays a label.
    enableSortingRemoval: false,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
          <p className="text-muted-foreground text-sm">
            {isLoading ? 'Loading…' : `${total.toLocaleString()} customer${total === 1 ? '' : 's'}`}
          </p>
        </div>

        <Input
          placeholder="Search name, email or phone…"
          value={search}
          onChange={(event) => applySearch(event.target.value)}
          className="w-72"
        />
      </div>

      <DataTable
        table={table}
        columnCount={columns.length}
        isLoading={isLoading}
        onRowActivate={(row) =>
          navigate({ to: '/customers/$customerId', params: { customerId: row.id } })
        }
        emptyMessage={filters.q ? 'No customers match that search.' : 'No customers yet.'}
      />

      <DataTablePagination table={table} total={total} />
    </div>
  );
}
